import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../utils/api';
import { identifyUser, resetUser, trackEvent } from '../utils/amplitude';

interface User {
  accidente: boolean;
  codePostal: string;
  id: string;
  email: string;
  name?: string;
  role: 'candidate' | 'employer' | 'admin' | 'collaborator';
  estBlackliste?: boolean;
  photoProfil?: string;
  prenom?: string;
  nom?: string;
  telephone?: string;
  ville?: string;
  titreProfession?: string;
  genre?: 'HOMME' | 'FEMME' | 'AUTRE';
  bio?: string;
  niveauExperience?: string;
  niveauEtude?: string;
  linkedin?: string;
  portfolioUrl?: string;
  recruteur?: { id: string; entrepriseId: string } | null;
  collaborateur?: { id: string; succursaleId?: string } | null;
  permissions?: string[];
  isFirstLogin?: boolean;
  derniereConnexion?: string;
  vues?: number;
  secteurActivite?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, role: 'candidate' | 'employer', remember?: boolean) => Promise<User>;
  staffLogin: (email: string, password: string, requiredRole: 'admin' | 'collaborator') => Promise<void>;
  register: (data: any) => Promise<void>;
  updateProfile: (data: any, file?: File) => Promise<void>;
  logout: () => void;
  loginWithToken: (token: string) => Promise<User>;
  forgotPassword: (email: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = () => {
      // Check both storages
      const storedToken = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('auth_user') || sessionStorage.getItem('auth_user');

      if (storedToken && storedUser) {
        setToken(storedToken);
        const parsedUser = JSON.parse(storedUser);
        
        // If collaborator, fetch fresh permissions and THEN set user/loading
        if (parsedUser.role === 'collaborator') {
          fetchCollaboratorPermissions().finally(() => {
            setIsLoading(false);
          });
        } else {
          setUser(parsedUser);
          
          // Check if employer profile is incomplete (missing recruteur info)
          if (parsedUser.role === 'employer' && !parsedUser.recruteur) {
            console.log('[Auth] Employer profile incomplete, refreshing...');
            try {
              loginWithToken(storedToken);
            } catch (e) {
              console.error('[Auth] Failed to pre-refresh profile:', e);
            }
          }
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  useEffect(() => {
    if (isLoading) return;

    if (user) {
      identifyUser(user.id, {
        email: user.email,
        role: user.role,
        name: user.name,
      });
    } else if (token === null) {
      // Only reset if we explicitly have no token (logged out or session expired)
      resetUser();
    }
  }, [user, isLoading, token]);

  // Inactivity timeout for Admin and Collaborator (10 minutes)
  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'collaborator')) return;

    let timeoutId: NodeJS.Timeout;

    let lastReset = Date.now();
    const resetTimer = () => {
      const now = Date.now();
      // On utilise un throttle de 30s en production pour la performance
      if (timeoutId && now - lastReset < 30000) return;
      
      lastReset = now;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.log('[Auth] Inactivity timeout reached. Logging out.');
        logout();
        // Redirection vers la page de connexion CRM
        window.location.href = '/crm/connexion';
      }, 10 * 60 * 1000); // 10 minutes réelles
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'voice_activity'];
    events.forEach(event => document.addEventListener(event, resetTimer));

    resetTimer(); // Initialize timer

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      events.forEach(event => document.removeEventListener(event, resetTimer));
    };
  }, [user]);

  const fetchCollaboratorPermissions = async () => {
    try {
      const response = await api.get('/collaborateurs/me/permissions');
      const permissions = response.data || response;
      
      setUser(prev => {
        if (!prev) {
          // If user was not set yet (e.g. initial load of collaborator), 
          // we might need to get it from storage first
          const storedUser = localStorage.getItem('auth_user') || sessionStorage.getItem('auth_user');
          if (storedUser) {
             const parsed = JSON.parse(storedUser);
             const updated = { ...parsed, permissions };
             return updated;
          }
          return null;
        }
        const updatedUser = { ...prev, permissions };
        // Update whichever storage is currently in use
        if (localStorage.getItem('auth_token')) {
          localStorage.setItem('auth_user', JSON.stringify(updatedUser));
        } else {
          sessionStorage.setItem('auth_user', JSON.stringify(updatedUser));
        }
        return updatedUser;
      });
      return permissions;
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      // Ensure permissions is at least an empty array to unblock UI skeleton
      setUser(prev => {
        if (!prev) return null;
        return { ...prev, permissions: [] };
      });
      return [];
    }
  };

  const normalizeUserData = (rawUserData: any): User => {
    const normalized = {
      id: rawUserData.id,
      email: rawUserData.email,
      name: rawUserData.prenom && rawUserData.nom ? `${rawUserData.prenom} ${rawUserData.nom}` : (rawUserData.name || rawUserData.email),
      role: rawUserData.role === 'RECRUTEUR' || rawUserData.role === 'EMPLOYEUR' ? 'employer' : 
            rawUserData.role === 'ADMIN' ? 'admin' : 
            rawUserData.role === 'COLLABORATEUR' ? 'collaborator' : 'candidate',
      photoProfil: rawUserData.photoProfil,
      prenom: rawUserData.prenom,
      nom: rawUserData.nom,
      telephone: rawUserData.telephone,
      ville: rawUserData.ville,
      titreProfession: rawUserData.titreProfession,
      genre: rawUserData.genre,
      bio: rawUserData.bio,
      niveauExperience: rawUserData.niveauExperience,
      niveauEtude: rawUserData.niveauEtude,
      linkedin: rawUserData.linkedin,
      portfolioUrl: rawUserData.portfolioUrl,
      secteurActivite: rawUserData.secteurActivite,
      recruteur: rawUserData.recruteur,
      collaborateur: rawUserData.collaborateur,
      permissions: rawUserData.permissions,
      estBlackliste: rawUserData.estBlackliste,
      isFirstLogin: rawUserData.isFirstLogin,
      derniereConnexion: rawUserData.derniereConnexion,
      accidente: rawUserData.accidente
    } as User;
    return normalized;
  };

  const login = async (email: string, password: string, role: 'candidate' | 'employer', remember: boolean = false) => {
    setIsLoading(true);
    try {
      const loginPayload = { email, password, role };
      const data = await api.post('/auth/login', loginPayload);
      
      const authData = data.data || data;
      const { access_token, user: rawUserData } = authData;

      if (!access_token || !rawUserData) {
        throw new Error('Données de connexion incomplètes');
      }

      const normalizedUser = normalizeUserData(rawUserData);

      // SÉCURITÉ : Si on utilise cette fonction de login "publique", 
      // on interdit strictement les rôles admin et collaborateur.
      if (normalizedUser.role === 'admin' || normalizedUser.role === 'collaborator') {
        throw new Error('Email ou mot de passe incorrect.');
      }

      const storage = remember ? localStorage : sessionStorage;
      
      // Clear other storage to avoid conflicts
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('auth_user');

      storage.setItem('auth_token', access_token);
      storage.setItem('auth_user', JSON.stringify(normalizedUser));
      
      // Save email for next time if remember is true
      if (remember) {
        localStorage.setItem('remembered_email', email);
      } else {
        localStorage.removeItem('remembered_email');
      }
      
      setToken(access_token);
      setUser(normalizedUser);
      return normalizedUser;
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.message || 'La connexion a échoué');
    } finally {
      setIsLoading(false);
    }
  };

  const staffLogin = async (email: string, password: string, requiredRole: 'admin' | 'collaborator') => {
    setIsLoading(true);
    try {
      const data = await api.post('/auth/login', { email, password });
      const authData = data.data || data;
      const { access_token, user: rawUserData } = authData;

      if (!access_token || !rawUserData) {
        throw new Error('Données de connexion incomplètes');
      }

      const normalizedUser = normalizeUserData(rawUserData);

      if (normalizedUser.role !== requiredRole) {
        throw new Error('Email ou mot de passe incorrect.');
      }

      // Staff login is usually always persistent for convenience
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('auth_user', JSON.stringify(normalizedUser));
      setToken(access_token);

      // Fetch permissions if collaborator BEFORE ending show loader
      if (normalizedUser.role === 'collaborator') {
        await fetchCollaboratorPermissions();
      } else {
        setUser(normalizedUser);
      }
    } catch (error: any) {
      console.error('Staff Login error:', error);
      throw new Error(error.message || 'Email ou mot de passe incorrect.');
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: any) => {
    setIsLoading(true);
    try {
      await api.post('/auth/register', data);
      trackEvent('Registration Successful', { role: data.role });
    } catch (error: any) {
      console.error('Registration error:', error);
      throw new Error(error.message || 'L\'inscription a échoué');
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (data: any, file?: File) => {
    try {
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (data[key] !== undefined && data[key] !== null) {
          formData.append(key, data[key]);
        }
      });

      if (file) {
        formData.append('photo', file);
      }

      const responseData = await api.patch('/users/profile', formData);
      
      const updatedRawUser = responseData.data || responseData;
      const normalizedUser = normalizeUserData(updatedRawUser);
      
      // Update in active storage
      if (localStorage.getItem('auth_token')) {
        localStorage.setItem('auth_user', JSON.stringify(normalizedUser));
      } else {
        sessionStorage.setItem('auth_user', JSON.stringify(normalizedUser));
      }
      setUser(normalizedUser);
    } catch (error: any) {
      console.error('Update profile error:', error);
      throw new Error(error.message || 'La mise à jour a échoué');
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('auth_user');
    setToken(null);
    setUser(null);
  };

  const loginWithToken = async (newToken: string) => {
    setIsLoading(true);
    try {
      localStorage.setItem('auth_token', newToken);
      setToken(newToken);
      
      // Fetch user profile using the new token
      const data = await api.get('/auth/me'); 
      const rawUserData = data.data || data;
      
      const normalizedUser = normalizeUserData(rawUserData);
      localStorage.setItem('auth_user', JSON.stringify(normalizedUser));
      setUser(normalizedUser);
      return normalizedUser;
    } catch (error: any) {
      console.error('Login with token error:', error);
      logout();
      throw new Error(error.message || 'La connexion par jeton a échoué');
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      await api.post('/auth/forgot-password', { email });
    } catch (error: any) {
      console.error('Forgot password error:', error);
      throw new Error(error.message || 'La demande a échoué');
    }
  };

  const value = React.useMemo(() => ({
    user,
    token,
    isAuthenticated: !!token,
    isLoading,
    login,
    staffLogin,
    register,
    updateProfile,
    logout,
    loginWithToken,
    forgotPassword,
  }), [user, token, isLoading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
